import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { CheckCircle2, XCircle } from "lucide-react";
import { verifyCertificate } from "@/lib/api/lxp-certificate.functions";

const qo = (token: string) =>
  queryOptions({ queryKey: ["lxp", "verify", token], queryFn: () => verifyCertificate({ data: { token } }) });

export const Route = createFileRoute("/lexpractiq/verify/$token")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(qo(params.token)),
  head: () => ({
    meta: [
      { title: "Certificate Verification | SA LexPractiq" },
      { name: "description", content: "Verify the authenticity of a SA LexPractiq certificate." },
      { property: "og:title", content: "Certificate Verification — SA LexPractiq" },
      { property: "og:description", content: "Public verification page for SA LexPractiq certificates." },
    ],
  }),
  errorComponent: () => (
    <div className="lxp-container" style={{ padding: "5rem 1.25rem", textAlign: "center" }}>
      <XCircle size={48} style={{ color: "var(--lxp-warn)" }} />
      <h1>Verification failed</h1>
    </div>
  ),
  notFoundComponent: () => (
    <div className="lxp-container" style={{ padding: "5rem 1.25rem", textAlign: "center" }}>
      <XCircle size={48} style={{ color: "var(--lxp-warn)" }} />
      <h1>Certificate not found</h1>
    </div>
  ),
  component: VerifyPage,
});

function VerifyPage() {
  const { token } = Route.useParams();
  const { data } = useSuspenseQuery(qo(token));
  if (!data.certificate) {
    return (
      <div className="lxp-container" style={{ padding: "5rem 1.25rem", textAlign: "center" }}>
        <XCircle size={48} style={{ color: "var(--lxp-warn)" }} />
        <h1>Not a valid certificate</h1>
        <p className="lxp-muted">This verification link is invalid or has expired.</p>
      </div>
    );
  }
  const c = data.certificate;
  return (
    <div className="lxp-container" style={{ padding: "3rem 1.25rem", maxWidth: 720 }}>
      <div className="lxp-card" style={{ textAlign: "center", padding: "2.5rem" }}>
        <CheckCircle2 size={56} style={{ color: "var(--lxp-accent)", marginBottom: "1rem" }} />
        <span className="lxp-badge">Verified</span>
        <h1 style={{ margin: "0.75rem 0 0.5rem" }}>{c.full_name}</h1>
        <p className="lxp-muted" style={{ margin: "0 0 1.5rem" }}>completed</p>
        <h2 style={{ margin: "0 0 0.5rem", fontSize: "1.25rem" }}>{c.simulation_title}</h2>
        <p className="lxp-muted" style={{ margin: "0 0 2rem" }}>
          Issued {new Date(c.issued_at).toLocaleDateString()} · Certificate {c.certificate_number}
          {c.final_score != null ? ` · Score ${c.final_score}%` : ""}
        </p>
        <p style={{ fontSize: "0.85rem", color: "var(--lxp-text-muted)" }}>
          This certificate was issued by SA LexPractiq — a Legal Experience Platform operated by Nkadimeng Danny Legal &amp; Compliance Support.
        </p>
      </div>
    </div>
  );
}
